import axios, { AxiosResponse } from 'axios';

import {
  InitiatePaymentProcessRequestData,
  InitiatePaymentRequestData,
  RetryPaymentData,
  RetryProcessAuthorisationResponse,
} from '../adapters/retryApiAdapter.types';
import { getDefaultConfig } from '../config/utils';
import { Dependencies } from '../dispatch';
import { Locale, ShopBundle } from '../types';

import { captureException } from './sentryService';

export const getRetryPaymentData = async ({
  domain,
  shopBundle,
  locale,
  orderId,
  customerId,
  originalUrl,
}: {
  domain: string;
  shopBundle: ShopBundle;
  locale: Locale;
  orderId: string;
  customerId: string;
  originalUrl: string;
}) => {
  try {
    const { retryPaymentBaseUrl } = getDefaultConfig();
    const result: AxiosResponse<RetryPaymentData> = await axios({
      method: 'get',
      url: `${retryPaymentBaseUrl}/${customerId}/retry-payment-data/${orderId}/${shopBundle}`,
      headers: {
        'Content-type': 'application/json',
        'X-Original-Host': domain,
        'Accept-Language': locale,
      },
      params: { originalURL: originalUrl },
    });
    if (!result) {
      throw new Error('Internal error while setting favorite card');
    }
    return result;
  } catch (error: any) {
    throw captureException({ error });
  }
};

export type RetryPaymentIdealData = {
  transactionId?: string;
  transactionUrl?: string;
};

export const getRetryPaymentIdealData = async ({
  orderId,
  domain,
  locale,
}: {
  orderId: string;
  domain: string;
  locale: Locale;
}) => {
  try {
    const result: AxiosResponse<RetryPaymentIdealData> = await axios({
      method: 'get',
      url: '/account/orders/idealRetryPaymentModel.htm',
      withCredentials: true,
      params: {
        orderId,
      },
      headers: {
        'Content-type': 'application/json',
        'X-Original-Host': domain,
        'Accept-Language': locale,
      },
    });
    if (!result) {
      throw new Error('Internal error while setting favorite card');
    }
    return result;
  } catch (error: any) {
    throw captureException({ error });
  }
};

export const getRetryData =
  ({
    orderId,
    customerId,
    shopBundle,
    pspResponseUrl,
    isAuthorizationResult,
  }: {
    orderId: string;
    customerId: string;
    shopBundle: ShopBundle;
    pspResponseUrl: string;
    isAuthorizationResult: boolean;
  }) =>
  async ({ retryApi, retrySuccessWithData, processPaymentAuthorisationSuccess }: Dependencies) => {
    try {
      const [retryPaymentData, processAuthorizationData] = await Promise.all([
        retryApi.getRetryPaymentData(customerId, orderId, shopBundle),
        isAuthorizationResult
          ? retryApi.processPaymentAuthorization({ customerId, pspResponseUrl })
          : new Promise((res) => res(undefined)),
      ]);

      retrySuccessWithData?.({
        ...retryPaymentData,
        idealAvailable: retryPaymentData.retryPaymentMethod === 'IDEAL',
      });

      if (processAuthorizationData) {
        processPaymentAuthorisationSuccess(
          processAuthorizationData as RetryProcessAuthorisationResponse,
        );
      }
    } catch (error: any) {
      //handle only presentational error ,
    }
  };

export const initiatePayment =
  (payload: InitiatePaymentRequestData) =>
  async ({
    retryApi,
    initiateRetryPaymentFailure,
    initiateRetryPaymentStart,
    initiateRetryPaymentSuccess,
  }: Dependencies) => {
    try {
      initiateRetryPaymentStart();
      const responseData = await retryApi.initiatePayment(payload);
      initiateRetryPaymentSuccess(
        responseData.action,
        String(responseData.paymentId),
        responseData.authorizationId,
      );
    } catch (error: any) {
      initiateRetryPaymentFailure();
    }
  };

export const processPaymentAuthorisation =
  (payload: InitiatePaymentProcessRequestData) =>
  async ({
    retryApi,
    processPaymentAuthorisationFailure,
    processPaymentAuthorisationStart,
    processPaymentAuthorisationSuccess,
  }: Dependencies) => {
    try {
      processPaymentAuthorisationStart();
      const responseData = await retryApi.processPaymentAuthorization(payload);
      processPaymentAuthorisationSuccess(responseData);
    } catch (error: any) {
      processPaymentAuthorisationFailure();
    }
  };
